/**
 * 💬 ChatWindow — 项目组聊天窗口
 *
 * 毛玻璃浮动面板，支持公共群聊和一对一私聊。
 * 消息类型：文字、语音（按住录音）、图片/视频/音频媒体。
 *
 * 架构设计（受控组件模式）：
 *   - chatTarget 由父组件 EditorPage 管理（通过 chatTarget + onChatTargetChange props）
 *   - null  → 公共聊天室（所有成员可见）
 *   - userId → 一对一私聊（仅双方可见）
 *   - 避免了内部状态与外部 prop 的同步问题
 *
 * 消息过滤逻辑：
 *   - Socket 推送的所有消息都存在 useChatSocket 的 messages 数组中
 *   - filteredMessages 根据当前 chatTarget 过滤显示：
 *     - 公共模式：只显示 recipient_id 为空的消息
 *     - 私聊模式：只显示「我→对方」和「对方→我」的消息
 *
 * 入口：EditorPage 底部标题栏的红色圆圈（公共）或成员头像（私聊）
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  X, Send, Mic, MessageCircle, Trash2,
  Image as ImageIcon, Film, Music, ChevronDown, Loader2, Volume2, PlusCircle,
  Play, Pause
} from 'lucide-react';
import UserAvatar from './UserAvatar';
import QuickTooltip from './QuickTooltip';
import useChatSocket from '../hooks/useChatSocket';
import { getVoiceShortcut, formatShortcut, VOICE_SHORTCUT_CHANGED } from '../hooks/useVoiceShortcut';
import api from '../api';

// =============================
// 样式常量
// =============================
const PANEL_W = 360;
const PANEL_MAX_H = '70vh';
const PANEL_BG = 'rgba(30, 41, 59, 0.92)';
const PANEL_BORDER = '1px solid rgba(255,255,255,0.12)';
const PANEL_RADIUS = 16;
const MSG_BG_SELF = 'rgba(59, 130, 246, 0.35)';
const MSG_BG_OTHER = 'rgba(255,255,255, 0.08)';

// =============================
// 辅助：格式化时间
// =============================
function formatTime(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (isToday) return `${hh}:${mm}`;
  const mm2 = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm2}/${dd} ${hh}:${mm}`;
}

// 两条消息之间是否需要显示时间分隔（>5 分钟）
function needTimeSep(prev, curr) {
  if (!prev) return true;
  return new Date(curr.created_at) - new Date(prev.created_at) > 5 * 60 * 1000;
}

// =============================
// ChatVoiceRecorder — 按住录音，松开发送（与 VoiceMicButton 同款交互）
// =============================
function ChatVoiceRecorder({ crewId, onSend, isOpen, isChatFocused }) {
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const durationRef = useRef(0);
  const pressActiveRef = useRef(false);
  const streamRef = useRef(null);
  const shortcutRef = useRef(getVoiceShortcut());

  // 同步快捷键配置
  useEffect(() => {
    const sync = () => { shortcutRef.current = getVoiceShortcut(); };
    window.addEventListener(VOICE_SHORTCUT_CHANGED, sync);
    return () => window.removeEventListener(VOICE_SHORTCUT_CHANGED, sync);
  }, []);

  // 上传并发送语音
  const uploadAndSend = useCallback(async (blob, dur) => {
    if (blob.size < 1000) return; // 太短忽略
    setUploading(true);
    try {
      const presign = await api.get(`/api/chat/voice-presign?crewId=${crewId}&filename=voice.webm&contentType=${encodeURIComponent('audio/webm')}`);
      await fetch(presign.presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'audio/webm' },
        body: blob,
      });
      onSend({
        type: 'voice',
        fileUrl: presign.objectUrl,
        fileMeta: { duration: dur, mimeType: 'audio/webm' },
      });
    } catch (err) {
      console.error('语音上传失败:', err);
    } finally {
      setUploading(false);
    }
  }, [crewId, onSend]);

  // 开始录音
  const startRecording = useCallback(async () => {
    if (pressActiveRef.current || recording) return;
    pressActiveRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // ⚠️ getUserMedia 是异步的，等待期间用户可能已经松开鼠标/按键。
      // 如果 pressActiveRef 已经被 stopRecording 重置为 false，则中止录音。
      if (!pressActiveRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const finalDur = durationRef.current;
        setRecording(false);
        setDuration(0);
        durationRef.current = 0;
        uploadAndSend(blob, finalDur);
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setRecording(true);
      setDuration(0);
      durationRef.current = 0;
      timerRef.current = setInterval(() => {
        durationRef.current += 1;
        setDuration(d => d + 1);
        // 60 秒自动停止
        if (durationRef.current >= 60) {
          clearInterval(timerRef.current);
          if (mediaRecorderRef.current?.state === 'recording') {
            pressActiveRef.current = false;
            mediaRecorderRef.current.stop();
          }
        }
      }, 1000);
    } catch (err) {
      console.error('无法访问麦克风:', err);
      clearInterval(timerRef.current); // 🛡️ H10: 防止残留计时器
      pressActiveRef.current = false;
    }
  }, [recording, uploadAndSend]);

  // 停止录音
  const stopRecording = useCallback(() => {
    if (!pressActiveRef.current) return;
    pressActiveRef.current = false;
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // 按住开始
  const handlePressStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    startRecording();
  }, [startRecording]);

  // 全局 mouseup/touchend → 停止
  // 注意：条件只用 pressActiveRef（ref 永远是最新值），不用 recording state，
  // 避免 getUserMedia 异步期间 recording 还是 false 导致松开鼠标后无法停止录音。
  useEffect(() => {
    const handleGlobalUp = () => {
      if (pressActiveRef.current) {
        stopRecording();
      }
    };
    document.addEventListener('mouseup', handleGlobalUp);
    document.addEventListener('touchend', handleGlobalUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalUp);
      document.removeEventListener('touchend', handleGlobalUp);
    };
  }, [stopRecording]);

  // 监听语音快捷键（与系统语音识别共用同一快捷键）
  useEffect(() => {
    if (!isOpen) return;

    function matchesShortcut(event, sc) {
      if (!sc) return false;
      const keyMatch = sc.code
        ? event.code === sc.code
        : event.key.toLowerCase() === (sc.key || '').toLowerCase();
      return keyMatch &&
        !!event.ctrlKey === !!sc.ctrlKey &&
        !!event.metaKey === !!sc.metaKey &&
        !!event.altKey === !!sc.altKey &&
        !!event.shiftKey === !!sc.shiftKey;
    }
    function isMainKeyUp(event, sc) {
      if (!sc) return false;
      return sc.code ? event.code === sc.code
        : event.key.toLowerCase() === (sc.key || '').toLowerCase();
    }

    const handleKeyDown = (e) => {
      const sc = shortcutRef.current;
      if (!sc || !matchesShortcut(e, sc)) return;
      // 聊天面板未被激活（用户最后一次点击在面板外），快捷键不归聊天室管
      if (isChatFocused && !isChatFocused.current) return;
      // 如果焦点在带 data-voice-shortcut 的 textarea，让系统快捷键处理
      const el = document.activeElement;
      if (el?.dataset?.voiceShortcut === 'true') return;
      // 聊天窗口内的 textarea 不带 data-voice-shortcut，我们处理
      if (pressActiveRef.current) { e.preventDefault(); return; }
      e.preventDefault();
      e.stopPropagation();
      startRecording();
    };

    const handleKeyUp = (e) => {
      const sc = shortcutRef.current;
      if (!sc || !isMainKeyUp(e, sc)) return;
      if (pressActiveRef.current) {
        stopRecording();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [isOpen, startRecording, stopRecording, isChatFocused]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  // 快捷键提示文字
  const shortcutLabel = useMemo(() => {
    const sc = getVoiceShortcut();
    return sc ? formatShortcut(sc) : null;
  }, []);

  if (uploading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, flexShrink: 0 }}>
        <Loader2 size={16} color="#94a3b8" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <div
        onMouseDown={handlePressStart}
        onTouchStart={handlePressStart}
        onContextMenu={e => e.preventDefault()}
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: recording ? 'rgba(239, 68, 68, 0.25)' : 'transparent',
          border: recording ? '2px solid rgba(239, 68, 68, 0.6)' : '1px solid transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.15s',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          touchAction: 'none',
          ...(recording ? { animation: 'voicePulse 1s ease-in-out infinite' } : {}),
        }}
        onMouseEnter={e => {
          if (!recording) { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }
        }}
        onMouseLeave={e => {
          if (!recording) { e.currentTarget.style.background = 'transparent'; }
        }}
        title={recording ? '录音中…松开结束' : `按住说话${shortcutLabel ? ` (${shortcutLabel})` : ''}`}
      >
        <Mic size={16} color={recording ? '#ef4444' : '#94a3b8'} strokeWidth={2} />
      </div>

      {/* 录音中浮层（同 VoiceMicButton 风格） */}
      {recording && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 10px)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: duration >= 55 ? 'rgba(234, 88, 12, 0.95)' : 'rgba(220, 38, 38, 0.92)',
          backdropFilter: 'blur(6px)',
          color: '#fff',
          fontSize: 12,
          fontWeight: 500,
          padding: '8px 16px 10px',
          borderRadius: 10,
          whiteSpace: 'nowrap',
          zIndex: 10002,
          pointerEvents: 'none',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}>
          <span>{duration >= 55 ? `即将结束，还剩 ${60 - duration}s` : '录音中 · 松开结束'}</span>
          {/* 声音波形 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: 24 }}>
            <div className="vbar vbar1" />
            <div className="vbar vbar2" />
            <div className="vbar vbar3" />
            <div className="vbar vbar4" />
            <div className="vbar vbar5" />
            <div className="vbar vbar6" />
            <div className="vbar vbar7" />
            <div className="vbar vbar8" />
          </div>
          <span style={{ fontSize: 10, opacity: 0.7 }}>{duration}s</span>
        </div>
      )}
    </div>
  );
}

// =============================
// VoiceMessageBar — 语音消息渲染
// =============================
function VoiceMessageBar({ fileUrl, fileMeta }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);  // 🆕 音频缓冲加载中
  const audioRef = useRef(null);
  const dur = fileMeta?.duration || 0;

  const ensureAudio = () => {
    if (!audioRef.current) {
      const audio = new Audio(fileUrl);
      audio.onended = () => setPlaying(false);
      // 🆕 音频开始实际播放时才显示进度条
      audio.onplaying = () => { setLoading(false); setPlaying(true); };
      // 🆕 加载失败或被中断时恢复状态
      audio.onerror = () => { setLoading(false); setPlaying(false); };
      audioRef.current = audio;
    }
    return audioRef.current;
  };

  const toggle = () => {
    const audio = ensureAudio();
    if (playing || loading) {
      audio.pause();
      audio.currentTime = 0;
      setPlaying(false);
      setLoading(false);
    } else {
      setLoading(true);  // 🆕 先显示加载状态
      audio.play().catch(() => { setLoading(false); });
      // 注意：不在这里 setPlaying(true)，而是等 onplaying 事件触发
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.onplaying = null;
        audioRef.current.onerror = null;
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <div onClick={toggle}
      style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '4px 0', minWidth: 80 }}>
      {loading
        ? <Loader2 size={14} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
        : <Volume2 size={14} color={playing ? '#3b82f6' : '#94a3b8'} />
      }
      <div style={{ flex: 1, height: 3, background: 'rgba(255,255,255,0.15)', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
        {loading && (
          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: '30%', background: '#3b82f6', borderRadius: 2, opacity: 0.6, animation: 'voiceBuffering 1s ease-in-out infinite' }} />
        )}
        {playing && (
          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', background: '#3b82f6', borderRadius: 2, animation: `voiceProgress ${dur}s linear forwards` }} />
        )}
      </div>
      <span style={{ fontSize: 10, color: '#94a3b8' }}>{loading ? '加载中' : `${dur}″`}</span>
    </div>
  );
}

// =============================
// MediaCard — 媒体消息缩略图
// =============================
// ──────────────────────────────────────────────────────────────────
// 聊天室视频单例播放机制
//
// 规则：聊天室中同一时刻只能有一个视频在播放。
// 实现方式：每个视频的 onPlay 触发时，遍历页面内所有 <video> 元素，
//   暂停除自身以外的其他视频。
// 优点：无需提升 state、无需 Context，DOM 级别互斥简单可靠。
//
// ⚠️ 修改注意：
//   - 不要给 video 加 autoplay，会破坏单例互斥逻辑
//   - 不要给 video 加 controls，聊天窗口空间有限，只保留播放/暂停覆层按钮
//   - 如需限定范围（只在当前聊天窗内），可通过 chatContainerRef 进一步限制
// ──────────────────────────────────────────────────────────────────

/**
 * 暂停页面内所有其他 <video> 元素（单例互斥播放）
 * @param {HTMLVideoElement} exceptEl - 当前正在播放的视频，不暂停它
 */
function stopOtherVideos(exceptEl) {
  document.querySelectorAll('video').forEach(v => {
    if (v !== exceptEl && !v.paused) v.pause();
  });
}

/**
 * ChatVideoOverlay — 聊天视频的播放/暂停覆层按钮
 *
 * 交互规则：
 *   - 暂停状态：半透明黑色遮罩 + ▶ 按钮，常驻可见，提示用户可点击播放
 *   - 播放状态：无遮罩，⏸ 按钮默认隐藏（CSS opacity:0），鼠标悬停时才显示
 *
 * CSS 类说明（定义在底部 <style> 中）：
 *   .chat-video-overlay        — 基础覆层
 *   .chat-video-overlay.playing .cv-btn  — 播放中时，按钮透明度归零
 *   .chat-video-overlay.playing:hover .cv-btn — 悬停时恢复可见
 */
function ChatVideoOverlay({ isPlaying, onToggle }) {
  return (
    <div
      onClick={onToggle}
      className={`chat-video-overlay${isPlaying ? ' playing' : ''}`}
      style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        // 暂停时加浅遮罩，让按钮更易识别；播放时完全透明不遮挡画面
        background: isPlaying ? 'transparent' : 'rgba(0,0,0,0.28)',
        borderRadius: 8,
        transition: 'background 0.2s',
      }}
    >
      {/* 圆形图标按钮 */}
      <div className="cv-btn" style={{
        width: 36, height: 36, borderRadius: '50%',
        background: 'rgba(0,0,0,0.6)',
        border: '1.5px solid rgba(255,255,255,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {isPlaying
          ? <Pause size={15} color="#fff" />          /* 播放中 → 显示暂停图标 */
          : <Play size={16} color="#fff" style={{ marginLeft: 2 }} />  /* 暂停中 → 显示播放图标 */
        }
      </div>
    </div>
  );
}

/**
 * MediaCard — 聊天消息中的媒体缩略图卡片
 *
 * 支持三种媒体类型：
 *   - image：静态图片，可拖拽到画布
 *   - video：视频缩略图，仅播放/暂停控制（无原生控制条）
 *   - audio：音频文件，使用原生 audio 控件
 *
 * 公共能力：
 *   - 可拖拽（onDragStart 携带媒体信息）
 *   - 「添加到画布」快捷按钮（需父组件传入 onAddToCanvas）
 *   - 左上角类型角标（图片蓝 / 视频黄）
 */
function MediaCard({ msg, onAddToCanvas }) {
  const isImage = msg.type === 'image';
  const isVideo = msg.type === 'video';
  const isAudio = msg.type === 'audio';
  const url = msg.file_url;

  const [loaded, setLoaded] = useState(false);      // 媒体资源是否已加载完毕
  const [isPlaying, setIsPlaying] = useState(false); // 视频当前播放状态（默认暂停）
  const videoRef = useRef(null);

  /**
   * 切换视频播放/暂停
   * - 播放前先暂停页面内其他所有视频（单例互斥）
   * - 使用 e.stopPropagation 防止触发父级拖拽事件
   */
  const togglePlay = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      stopOtherVideos(video); // 互斥：先暂停页面内其他视频
      video.play().catch(() => {}); // 忽略自动播放策略报错
    }
    setIsPlaying(!isPlaying);
  };

  // 可拖拽至画布
  const handleDragStart = (e) => {
    const dragData = {
      source: 'chat',
      type: msg.type,
      url: url,
      messageId: msg.id,
      // 如果有 node_data，附上
      ...(msg.node_data || {}),
    };
    e.dataTransfer.setData('application/cineart-media', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  // 添加到画布按钮
  const addBtn = onAddToCanvas ? (
    <div
      onClick={(e) => { e.stopPropagation(); onAddToCanvas({ type: msg.type, url }); }}
      style={{
        position: 'absolute', bottom: 4, right: 4,
        background: 'rgba(59, 130, 246, 0.85)', borderRadius: 6,
        padding: '3px 8px', fontSize: 10, color: '#fff',
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
        opacity: 0.85, transition: 'opacity 0.15s',
        backdropFilter: 'blur(4px)',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '1'}
      onMouseLeave={e => e.currentTarget.style.opacity = '0.85'}
      title="添加到画布"
    >
      <PlusCircle size={11} /> 添加到画布
    </div>
  ) : null;

  // 类型角标（与 CustomNode 同款配色：图片蓝 #3b82f6 / 视频黄 #fbbf24）
  const typeBadge = (isImage || isVideo) ? (
    <div style={{
      position: 'absolute', top: 4, left: 4,
      background: 'rgba(0,0,0,0.55)', borderRadius: 4,
      padding: '2px 6px', fontSize: 9, display: 'flex', alignItems: 'center', gap: 3,
      color: isImage ? '#3b82f6' : '#fbbf24',
      backdropFilter: 'blur(4px)',
    }}>
      {isImage ? <ImageIcon size={9} /> : <Film size={9} />}
      <span style={{ color: '#fff' }}>{isImage ? '图片' : '视频'}</span>
    </div>
  ) : null;

  if (isImage) {
    return (
      <div draggable onDragStart={handleDragStart} style={{ cursor: 'grab', borderRadius: 8, overflow: 'hidden', maxWidth: 200, position: 'relative', background: '#1e1e2e' }}>
        {!loaded && (
          <div className="shimmer-wrapper" style={{ width: 200, height: 120, borderRadius: 8 }} />
        )}
        <img src={url} alt=""
          style={{ width: '100%', borderRadius: 8, display: loaded ? 'block' : 'none', transition: 'opacity 0.3s' }}
          onLoad={() => setLoaded(true)}
          onError={e => { e.target.style.display = 'none'; setLoaded(true); }} />
        {typeBadge}
        {addBtn}
      </div>
    );
  }

  if (isVideo) {
    return (
      <div draggable onDragStart={handleDragStart}
        style={{ cursor: 'grab', borderRadius: 8, overflow: 'hidden', maxWidth: 200, position: 'relative', background: '#000' }}>

        {/* 加载前骨架屏展位（placeholder shimmer） */}
        {!loaded && (
          <div className="shimmer-wrapper" style={{ width: 200, height: 120, borderRadius: 8 }} />
        )}

        {/*
          视频元素说明：
          - 禁用 controls：聊天窗口内空间有限，原生控制条（音量/进度/全屏）均不使用
          - preload="metadata"：只预加载封面信息，不自动拉取视频流
          - onEnded / onPause：同步播放状态至 React state
        */}
        <video
          ref={videoRef}
          src={url}
          style={{ width: '100%', borderRadius: 8, display: loaded ? 'block' : 'none', transition: 'opacity 0.3s' }}
          preload="metadata"
          onLoadedData={() => setLoaded(true)}
          onEnded={() => setIsPlaying(false)}
          onPause={() => setIsPlaying(false)}
        />

        {/* 播放/暂停覆层：资源加载完成同展示 */}
        {loaded && (
          <ChatVideoOverlay isPlaying={isPlaying} onToggle={togglePlay} />
        )}

        {typeBadge}
        {addBtn}
      </div>
    );
  }

  if (isAudio) {
    return (
      <div draggable onDragStart={handleDragStart}
        style={{ cursor: 'grab', display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '6px 10px', position: 'relative' }}>
        <Music size={14} color="#10b981" />
        <audio controls src={url} style={{ height: 28, maxWidth: 180 }} />
        {addBtn}
      </div>
    );
  }

  return null;
}

// =============================
// MessageBubble — 单条消息
// =============================
function MessageBubble({ msg, isSelf, onDelete, user, onAddToCanvas }) {
  const [showActions, setShowActions] = useState(false);
  const canDelete = isSelf && (Date.now() - new Date(msg.created_at).getTime() < 2 * 60 * 1000);

  return (
    <div
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      style={{
        display: 'flex',
        flexDirection: isSelf ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: 8,
        padding: '2px 0',
        position: 'relative',
      }}
    >
      {/* 头像 */}
      <UserAvatar avatar={msg.sender_avatar} username={msg.sender_name} size={28} fontSize={11} />

      {/* 内容区 */}
      <div style={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', alignItems: isSelf ? 'flex-end' : 'flex-start' }}>
        {/* 发送者名字 */}
        {!isSelf && (
          <span style={{ fontSize: 10, color: '#64748b', marginBottom: 2, paddingLeft: 2 }}>{msg.sender_name}</span>
        )}

        {/* 消息内容 */}
        <div style={{
          background: isSelf ? MSG_BG_SELF : MSG_BG_OTHER,
          borderRadius: isSelf ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
          padding: msg.type === 'text' || msg.type === 'voice' ? '8px 12px' : '4px',
          fontSize: 13,
          color: '#e2e8f0',
          lineHeight: 1.5,
          wordBreak: 'break-word',
          position: 'relative',
        }}>
          {msg.type === 'text' && msg.content}
          {msg.type === 'voice' && <VoiceMessageBar fileUrl={msg.file_url} fileMeta={msg.file_meta} />}
          {(msg.type === 'image' || msg.type === 'video' || msg.type === 'audio') && <MediaCard msg={msg} onAddToCanvas={onAddToCanvas} />}
        </div>

        {/* 时间 */}
        <span style={{ fontSize: 9, color: '#475569', marginTop: 2, paddingLeft: 2 }}>{formatTime(msg.created_at)}</span>
      </div>

      {/* 操作按钮（撤回） */}
      {showActions && canDelete && (
        <QuickTooltip content="撤回" position="top" delay={200}>
          <button onClick={() => onDelete(msg.id)}
            style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 4, padding: 3, cursor: 'pointer', color: '#fca5a5', display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'center' }}>
            <Trash2 size={12} />
          </button>
        </QuickTooltip>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
//  ChatWindow — 主组件
// ═══════════════════════════════════════════════════════

export default function ChatWindow({ crewId, crewMembers, user, onClose, onAddToCanvas, chatTarget, onChatTargetChange, onChatFocus }) {
  // ── 受控状态：chatTarget 由 EditorPage 的 chatTargetId 驱动 ──
  // null = 公共聊天室 | user_id(number) = 一对一私聊
  // 内部统一用 setChatTarget 调用，实际修改的是父组件的 chatTargetId
  const setChatTarget = onChatTargetChange;
  const [inputText, setInputText] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  // ── 焦点管理：决定快捷键归属 ──
  // 用户在聊天面板内 mousedown → chatFocusedRef = true，快捷键归聊天室
  // 用户在面板外 mousedown → chatFocusedRef = false，快捷键归画布节点
  const chatFocusedRef = useRef(false);
  const chatPanelRef = useRef(null);
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (chatPanelRef.current && !chatPanelRef.current.contains(e.target)) {
        chatFocusedRef.current = false;
      }
    };
    // capture=true：在任何 stopPropagation 之前执行，确保能检测到面板外的点击
    document.addEventListener('mousedown', handleOutsideClick, true);
    return () => document.removeEventListener('mousedown', handleOutsideClick, true);
  }, []);

  // ── Socket hook ──
  const {
    messages, typingUsers, connected, loading, hasMore,
    loadHistory, loadInitial, sendMessage, sendTyping, deleteMessage,
  } = useChatSocket({ crewId, user, isOpen: true });

  // ── 当前私聊对象的用户信息（用于显示标题「与 xxx」） ──
  const targetUser = useMemo(() =>
    chatTarget ? crewMembers.find(m => m.user_id === chatTarget) : null,
    [chatTarget, crewMembers]
  );

  // ── 消息过滤：根据 chatTarget 分离公共消息和私聊消息 ──
  // Socket 收到的所有消息混在同一个 messages 数组里，
  // 这里按 recipient_id 过滤出当前视图应显示的消息。
  const filteredMessages = useMemo(() => {
    if (chatTarget) {
      // 私聊：只保留「我发给对方」+「对方发给我」的消息
      return messages.filter(m =>
        (m.sender_id === user?.id && m.recipient_id === chatTarget) ||
        (m.sender_id === chatTarget && m.recipient_id === user?.id)
      );
    } else {
      // 公共聊天：只保留 recipient_id 为空的群发消息
      return messages.filter(m => !m.recipient_id);
    }
  }, [messages, chatTarget, user]);

  // ── 初始加载 ──
  useEffect(() => {
    loadInitial(chatTarget);
  }, [chatTarget, loadInitial]);

  // 记录上一次 filteredMessages 的长度，用于区分"初次加载"和"新消息到来"
  const prevFilteredLengthRef = useRef(0);

  // 切换聊天对象时重置计数，下次消息加载一样视为初次加载
  useEffect(() => {
    prevFilteredLengthRef.current = 0;
  }, [chatTarget]);

  // ── 自动滚动到底部 ──
  useEffect(() => {
    if (!autoScroll) return;
    const newLen = filteredMessages.length;
    const prevLen = prevFilteredLengthRef.current;
    prevFilteredLengthRef.current = newLen;

    if (newLen === 0) return;

    if (prevLen === 0) {
      // 初次加载（从空变为有消息）：直接设 scrollTop，无任何动画
      // 用户打开窗口时直接看到最新一条，需要历史记录时自己向上翻
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    } else if (newLen > prevLen) {
      // 收到新消息：平滑滚动到底部
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filteredMessages, autoScroll]);

  // ── 监听滚动位置，判断是否在底部附近 ──
  const handleScroll = useCallback((e) => {
    const el = e.target;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setAutoScroll(nearBottom);

    // 滚到顶部时加载更多
    if (el.scrollTop < 40 && hasMore && !loading) {
      const prevHeight = el.scrollHeight;
      loadHistory(chatTarget).then(() => {
        // 保持滚动位置
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight - prevHeight;
        });
      });
    }
  }, [hasMore, loading, loadHistory, chatTarget]);

  // ── 发送文本消息（智能识别云存储媒体链接） ──
  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text) return;

    // 检测是否为媒体 URL（整条都是一个 URL，按文件扩展名判断类型）
    const urlPattern = /^https?:\/\/\S+$/i;
    if (urlPattern.test(text)) {
      const lower = text.toLowerCase();
      let mediaType = null;
      if (/\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(lower)) mediaType = 'image';
      else if (/\.(mp4|webm|mov|avi|mkv)(\?|$)/i.test(lower)) mediaType = 'video';
      else if (/\.(mp3|wav|ogg|aac|flac|m4a)(\?|$)/i.test(lower)) mediaType = 'audio';

      if (mediaType) {
        sendMessage({
          type: mediaType,
          fileUrl: text,
          fileMeta: { shared: true, mimeType: mediaType === 'image' ? 'image/jpeg' : mediaType === 'video' ? 'video/mp4' : 'audio/mpeg' },
          recipientId: chatTarget,
        });
        setInputText('');
        setAutoScroll(true);
        inputRef.current?.focus();
        return;
      }
    }

    sendMessage({
      type: 'text',
      content: text,
      recipientId: chatTarget,
    });
    setInputText('');
    setAutoScroll(true);
    inputRef.current?.focus();
  }, [inputText, chatTarget, sendMessage]);

  // ── 发送语音消息 ──
  const handleVoiceSend = useCallback((voiceData) => {
    sendMessage({
      ...voiceData,
      recipientId: chatTarget,
    });
    setAutoScroll(true);
  }, [chatTarget, sendMessage]);

  // ── 消息撤回 ──
  const handleDelete = useCallback(async (msgId) => {
    try { await deleteMessage(msgId); } catch { /* ignore */ }
  }, [deleteMessage]);

  // ── 键盘事件 ──
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else {
      sendTyping();
    }
  }, [handleSend, sendTyping]);

  // ── 滚动到底部的按钮 ──
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setAutoScroll(true);
  }, []);

  // ── 其他成员列表（排除自己，用于成员选择栏） ──
  // 项目负责人排在前面，依赖 crewMembers 中的 user_id 字段（由后端 crews.js 返回）
  const otherMembers = useMemo(() =>
    (crewMembers || [])
      .filter(m => m.user_id !== user?.id)
      .sort((a, b) => (a.role === 'producer' ? -1 : b.role === 'producer' ? 1 : 0)),
    [crewMembers, user]
  );

  // ── 正在输入提示文本 ──
  const typingText = useMemo(() => {
    if (typingUsers.length === 0) return null;
    if (typingUsers.length === 1) return `${typingUsers[0].username} 正在输入...`;
    return `${typingUsers.length} 人正在输入...`;
  }, [typingUsers]);

  return (
    <div
      style={{
        position: 'fixed',
        left: 80,
        bottom: 80,
        width: PANEL_W,
        maxHeight: PANEL_MAX_H,
        background: PANEL_BG,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: PANEL_BORDER,
        borderRadius: PANEL_RADIUS,
        boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10001,
        overflow: 'hidden',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => {
        chatFocusedRef.current = true;
        onChatFocus?.(); // 通知父组件取消画布节点的选中状态，避免快捷键冲突
        e.stopPropagation();
      }}
      ref={chatPanelRef}
    >
      {/* ─── 顶栏 ─── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MessageCircle size={16} color="#3b82f6" />
          <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>
            {targetUser ? `与 ${targetUser.username}` : '公共聊天室'}
          </span>
          {/* 连接状态指示 */}
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: connected ? '#10b981' : '#ef4444', flexShrink: 0 }} />
        </div>
        <button onClick={onClose}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2, display: 'flex', borderRadius: 4, transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = '#fff'}
          onMouseLeave={e => e.currentTarget.style.color = '#64748b'}>
          <X size={16} />
        </button>
      </div>

      {/* ─── 成员选择栏（公共/私聊切换） ─── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        overflowX: 'auto',
        flexShrink: 0,
      }}>
        {/* 公共聊天室按钮 — 红色圆圈，点击 setChatTarget(null) 切换到公共模式 */}
        <QuickTooltip content="公共聊天室" position="top" delay={300}>
          <button
            onClick={() => setChatTarget(null)}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.85)',
              border: chatTarget === null ? '2px solid #ef4444' : '2px solid transparent',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s',
              flexShrink: 0,
              boxShadow: chatTarget === null ? '0 0 0 2px rgba(239,68,68,0.3)' : 'none',
              opacity: chatTarget === null ? 1 : 0.8,
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={e => { if (chatTarget !== null) e.currentTarget.style.opacity = '0.8'; }}
          >
            <MessageCircle size={14} color="#fff" />
          </button>
        </QuickTooltip>

        {/* 成员头像按钮 — 点击 setChatTarget(member.user_id) 切换到私聊模式 */}
        {otherMembers.map(member => {
          const isActive = chatTarget === member.user_id;
          return (
            <QuickTooltip key={member.user_id} content={`私聊 ${member.username}`} position="top" delay={300}>
              <button
                onClick={() => setChatTarget(member.user_id)}
                style={{
                  background: 'transparent',
                  border: isActive ? '2px solid #3b82f6' : '2px solid transparent',
                  borderRadius: '50%',
                  padding: 0,
                  cursor: 'pointer',
                  transition: 'border-color 0.15s',
                  flexShrink: 0,
                  opacity: isActive ? 1 : 0.7,
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.opacity = '0.7'; }}>
                <UserAvatar
                  avatar={member.avatar}
                  username={member.username}
                  size={28}
                  fontSize={11}
                />
              </button>
            </QuickTooltip>
          );
        })}
      </div>

      {/* ─── 消息列表 ─── */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '10px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          minHeight: 200,
          position: 'relative',
        }}
      >
        {/* 加载更多指示器 */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 8 }}>
            <Loader2 size={16} color="#64748b" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        {/* 无消息 */}
        {!loading && filteredMessages.length === 0 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#475569' }}>
            <MessageCircle size={32} strokeWidth={1} />
            <span style={{ fontSize: 12 }}>
              {targetUser ? `与 ${targetUser.username} 的私聊` : '项目组群聊'}
            </span>
            <span style={{ fontSize: 11, color: '#374151' }}>发送第一条消息吧！</span>
          </div>
        )}

        {/* 消息列表 */}
        {filteredMessages.map((msg, idx) => {
          const prev = idx > 0 ? filteredMessages[idx - 1] : null;
          const isSelf = msg.sender_id === user?.id;
          return (
            <React.Fragment key={msg.id}>
              {/* 时间分隔 */}
              {needTimeSep(prev, msg) && (
                <div style={{ textAlign: 'center', padding: '6px 0', fontSize: 10, color: '#475569' }}>
                  {formatTime(msg.created_at)}
                </div>
              )}
              <MessageBubble msg={msg} isSelf={isSelf} onDelete={handleDelete} user={user} onAddToCanvas={onAddToCanvas} />
            </React.Fragment>
          );
        })}

        {/* 正在输入提示 */}
        {typingText && (
          <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic', padding: '2px 4px' }}>
            {typingText}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 滚动到底部按钮 */}
      {!autoScroll && (
        <button onClick={scrollToBottom}
          style={{
            position: 'absolute',
            right: 16,
            bottom: 70,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'rgba(30,41,59,0.95)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#94a3b8',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            zIndex: 2,
          }}>
          <ChevronDown size={16} />
        </button>
      )}

      {/* ─── 输入区域 ─── */}
      <div style={{
        padding: '10px 14px',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
      }}>
        {/* 文本输入 */}
        <div style={{ flex: 1, position: 'relative' }}>
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={targetUser ? `发送给 ${targetUser.username}...` : '发送到群聊...'}
            rows={1}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10,
              padding: '8px 12px',
              color: '#e2e8f0',
              fontSize: 13,
              resize: 'none',
              outline: 'none',
              lineHeight: 1.4,
              maxHeight: 80,
              overflow: 'auto',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              transition: 'border-color 0.15s',
            }}
            onFocus={e => e.target.style.borderColor = 'rgba(59,130,246,0.5)'}
            onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
          />
        </div>

        {/* 语音录制（按住说话） */}
        <ChatVoiceRecorder crewId={crewId} onSend={handleVoiceSend} isOpen={true} isChatFocused={chatFocusedRef} />

        {/* 发送按钮 */}
        <button onClick={handleSend} disabled={!inputText.trim()}
          style={{
            background: inputText.trim() ? 'rgba(59,130,246,0.8)' : 'rgba(255,255,255,0.06)',
            border: 'none',
            borderRadius: 8,
            padding: '8px 10px',
            cursor: inputText.trim() ? 'pointer' : 'default',
            color: inputText.trim() ? '#fff' : '#475569',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s',
            flexShrink: 0,
          }}>
          <Send size={16} />
        </button>
      </div>

      {/* ─── 动画与模块局部样式 ─── */}
      <style>{`
        /* 通用动画 */
        @keyframes spin   { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse  { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

        /* 聊天视频播放覆层（ChatVideoOverlay）
         * .playing         — 添加到覆层 div 上，表示播放中
         * .cv-btn          — 图标按钮
         * playing .cv-btn  — 播放时隐藏按钮
         * playing:hover    — 悬停时亮起按钮，提示可暂停
         */
        .chat-video-overlay .cv-btn          { transition: opacity 0.2s; }
        .chat-video-overlay.playing .cv-btn  { opacity: 0; }
        .chat-video-overlay.playing:hover .cv-btn { opacity: 1; }

        /* 语音播放进度条动画 */
        @keyframes voiceProgress   { from { width: 0%; } to { width: 100%; } }
        @keyframes voiceBuffering  { 0% { left: -30%; } 100% { left: 100%; } }

        /* 麦克录音按钮脉冲动画 */
        @keyframes voicePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          50%       { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
        }

        /* 录音中语音波形条（共 8 根，各自随机延迟营造该动感） */
        .vbar { width: 3px; border-radius: 2px; background: rgba(255,255,255,0.85); }
        @keyframes voiceBar1 { 0%,100%{height:6px}  50%{height:18px} }
        @keyframes voiceBar2 { 0%,100%{height:10px} 50%{height:22px} }
        @keyframes voiceBar3 { 0%,100%{height:14px} 50%{height:8px}  }
        @keyframes voiceBar4 { 0%,100%{height:8px}  50%{height:20px} }
        @keyframes voiceBar5 { 0%,100%{height:16px} 50%{height:6px}  }
        @keyframes voiceBar6 { 0%,100%{height:10px} 50%{height:18px} }
        @keyframes voiceBar7 { 0%,100%{height:6px}  50%{height:14px} }
        @keyframes voiceBar8 { 0%,100%{height:12px} 50%{height:20px} }
        .vbar1 { animation: voiceBar1 0.80s ease-in-out infinite; }
        .vbar2 { animation: voiceBar2 0.70s ease-in-out infinite 0.10s; }
        .vbar3 { animation: voiceBar3 0.90s ease-in-out infinite 0.05s; }
        .vbar4 { animation: voiceBar4 0.75s ease-in-out infinite 0.15s; }
        .vbar5 { animation: voiceBar5 0.85s ease-in-out infinite 0.08s; }
        .vbar6 { animation: voiceBar6 0.70s ease-in-out infinite 0.12s; }
        .vbar7 { animation: voiceBar7 0.95s ease-in-out infinite 0.03s; }
        .vbar8 { animation: voiceBar8 0.80s ease-in-out infinite 0.18s; }
      `}</style>
    </div>
  );
}
